import { v4 as uuid } from 'uuid';
import { PendingOperationEntry, PendingOperationScope } from '../types/patchTypes';
import {
  getOperationConflictKey,
  getOperationKey,
  normalizeOperationNode,
  readWeightId,
} from './operationIdentity';
import { mapPatchOperations } from './mapPatchOperations';
import { WeightPriceRange } from './priceRanges';

/** A `{ minimal, enriched }` pair, the wrapper every pending operation is added as. */
export interface OperationWrapper {
  minimal: any;
  enriched: any;
}

/**
 * What an edit replaces on an operation.
 *
 * Two shapes exist because two kinds of value do: most priced operations carry one amount, while
 * `setModifierPriceRanges` carries a list. A bare number is still accepted for the amount case, which is
 * every caller that predates ranges being editable.
 */
export interface OperationEdit {
  /** Replacement amount, in whole cents. */
  amount?: number;
  /** Replacement price ranges, for `setModifierPriceRanges`. */
  weightPriceRanges?: WeightPriceRange[];
}

const toEdit = (edit?: number | OperationEdit): OperationEdit =>
  typeof edit === 'number' ? { amount: edit } : edit ?? {};

/** Reads an entity id whether it is nested under the entity or flat on the operation. */
const readId = (data: any, entity: string, field: string): string | undefined =>
  data?.[entity]?.[field] ?? data?.[field];

/**
 * Builds the API-shaped minimal operation for a patch operation node.
 *
 * Needed because a node from `getPatch` is not a request body: its ids are nested under entity objects
 * whose key fields do not always match the input field names - a modifier weight is the sharp edge, see
 * readWeightId. Fields are therefore read explicitly rather than spread, so a missing rename shows up
 * here rather than as an `undefined` id on the wire.
 *
 * `edit` overrides the value the node already carries - an amount in whole cents, or a replacement set of
 * price ranges. Omit it to keep what the node holds, which is what a deletion wants.
 *
 * Returns null for operation types this flow does not support.
 */
export const toMinimalOperation = (node: any, edit?: number | OperationEdit): any | null => {
  const normalized = normalizeOperationNode(node);
  if (!normalized) return null;

  const { key, data } = normalized;
  const { amount, weightPriceRanges } = toEdit(edit);

  const variantId = readId(data, 'variant', 'variantId');
  const slotId = readId(data, 'slot', 'slotId');
  const modifierId = readId(data, 'modifier', 'modifierId');
  const bundleId = readId(data, 'bundle', 'bundleId');
  const productId = readId(data, 'product', 'productId');

  // An explicit amount wins; otherwise keep whatever the node holds.
  const resolvedAmount = amount ?? data.amount ?? data.authoringPrice?.amount;
  const authoringPrice = resolvedAmount === undefined ? undefined : { amount: resolvedAmount };

  switch (key) {
    case 'setNamedPriceAmount':
      return {
        setNamedPriceAmount: {
          namedPriceId: readId(data, 'namedPrice', 'namedPriceId'),
          ...(resolvedAmount !== undefined && { amount: resolvedAmount }),
        },
      };

    case 'setVariantPrice':
      return { setVariantPrice: { variantId, ...(authoringPrice && { authoringPrice }) } };

    case 'setBundlePrice':
      return { setBundlePrice: { bundleId, ...(authoringPrice && { authoringPrice }) } };

    case 'setModifierWeightPrice':
      return {
        setModifierWeightPrice: {
          variantId,
          slotId,
          modifierId,
          weightId: readWeightId(data),
          ...(authoringPrice && { authoringPrice }),
        },
      };

    case 'setModifierPriceRanges':
      return {
        setModifierPriceRanges: {
          variantId,
          slotId,
          weightPriceRanges: weightPriceRanges ?? data.weightPriceRanges ?? [],
        },
      };

    case 'setModifierWeightIsExcludedFromVariant':
      return {
        setModifierWeightIsExcludedFromVariant: {
          variantId,
          slotId,
          modifierId,
          weightId: readWeightId(data),
          isExcludedFromVariant: data.isExcludedFromVariant ?? data.isExcluded ?? true,
        },
      };

    case 'removeProduct':
      return { removeProduct: { productId } };

    case 'removeVariant':
      return { removeVariant: { variantId } };

    case 'removeModifier':
      return { removeModifier: { modifierId } };

    case 'removeModifierFromSlot':
      return { removeModifierFromSlot: { slotId, modifierId } };

    case 'removeWeightFromSlotModifier':
      return { removeWeightFromSlotModifier: { slotId, modifierId, weightId: readWeightId(data) } };

    case 'removeBundle':
      return { removeBundle: { bundleId } };

    default:
      return null;
  }
};

/**
 * The `__typename` a pending operation's enriched half carries, per operation key. The inverse of
 * `TYPENAME_TO_KEY`, and the shape `PendingChangesView` matches on.
 */
const KEY_TO_TYPENAME: Record<string, string> = {
  setVariantPrice: 'SetVariantPriceMenuPatchOperation',
  setBundlePrice: 'SetBundlePriceMenuPatchOperation',
  setNamedPriceAmount: 'SetNamedPriceAmountPatchOperation',
  setModifierPriceRanges: 'SetModifierPriceRangesMenuPatchOperation',
  setModifierWeightPrice: 'SetModifierWeightPriceMenuPatchOperation',
  setModifierWeightIsExcludedFromVariant: 'SetModifierWeightIsExcludedFromVariantMenuPatchOperation',
  removeProduct: 'RemoveProductMenuPatchOperation',
  removeVariant: 'RemoveVariantMenuPatchOperation',
  removeModifier: 'RemoveModifierMenuPatchOperation',
  removeModifierFromSlot: 'RemoveModifierFromSlotPatchOperation',
  removeWeightFromSlotModifier: 'RemoveWeightFromSlotModifierMenuPatchOperation',
  removeBundle: 'RemoveBundleMenuPatchOperation',
};

/**
 * Converts a `getPatch` node into the nested enriched shape the rest of the app stores.
 *
 * Every existing consumer - `PendingChangesView` above all - reads an enriched operation as
 * `{ __typename, [inputKey]: { ...entities } }`. A node keeps its fields directly under the typename
 * instead, so storing one verbatim makes the pending changes list fail to recognise it and render
 * "Unknown operation type". The entity objects are carried across untouched, so the names the payload
 * embedded survive.
 */
export const toEnrichedOperation = (node: any): any | null => {
  const normalized = normalizeOperationNode(node);
  if (!normalized) return null;

  const { key, data } = normalized;

  // Already nested - the drawer built it, so leave it exactly as it is.
  if (node[key]) return node;

  const { __typename, patchOperationId, ...fields } = data;

  return {
    __typename: KEY_TO_TYPENAME[key] || node.__typename,
    ...(patchOperationId && { patchOperationId }),
    [key]: fields,
  };
};

/**
 * Wraps a patch operation node as a `{ minimal, enriched }` pair.
 *
 * The minimal half is the request body. The enriched half is normalised to the nested shape the
 * display expects, carrying whatever names the payload embedded.
 */
export const wrapOperationNode = (node: any, edit?: number | OperationEdit): OperationWrapper | null => {
  const minimal = toMinimalOperation(node, edit);
  if (!minimal) return null;

  const built = toEnrichedOperation(node);
  if (!built) return null;

  // Copied because toEnrichedOperation returns a drawer-built node as-is, and reflecting the edit below
  // would otherwise write through to the caller's object.
  const enriched = { ...built };
  const { amount, weightPriceRanges } = toEdit(edit);
  const key = getOperationKey(minimal);

  // Reflect the edit in the enriched half too, or the display would show the pre-edit value.
  if (key && enriched[key]) {
    if (weightPriceRanges !== undefined && key === 'setModifierPriceRanges') {
      enriched[key] = { ...enriched[key], weightPriceRanges };
    } else if (amount !== undefined) {
      enriched[key] =
        key === 'setNamedPriceAmount'
          ? { ...enriched[key], amount }
          : { ...enriched[key], authoringPrice: { ...(enriched[key].authoringPrice || {}), amount } };
    }
  }

  return { minimal, enriched };
};

/**
 * Wraps a node as a *reference* to its operation - identifiers only, no value - which is how removal is
 * expressed. Runs through `mapPatchOperations`' removal mode rather than restating which fields survive,
 * so there is one definition of the reference shape. Applying it again at submit is idempotent.
 */
export const wrapOperationNodeForRemoval = (node: any): OperationWrapper | null => {
  const minimal = toMinimalOperation(node);
  if (!minimal) return null;

  const enriched = toEnrichedOperation(node);
  if (!enriched) return null;

  const [reference] = mapPatchOperations([minimal], true);
  return { minimal: reference, enriched };
};

/** Builds a complete pending entry, filling in the id and the conflict key. */
export const buildPendingEntry = (
  op: OperationWrapper,
  scope: PendingOperationScope,
  previousValue?: number
): PendingOperationEntry => ({
  opId: uuid(),
  minimal: op.minimal,
  enriched: op.enriched,
  conflictKey: getOperationConflictKey(op.minimal),
  scope,
  previousValue,
});
